import config from '@plone/volto/registry';

import { isPloneContentUrl, normalizeAppUrl } from './appUrl';

const normalizeImageScalePath = (path) =>
  path.replace(/\/@images\//g, '/@@images/');

const stripApiMount = (path) => {
  if (path.startsWith('/++api++/Plone')) {
    return path.slice('/++api++/Plone'.length) || '/';
  }
  if (path.startsWith('/++api++')) {
    return path.slice('/++api++'.length) || '/';
  }
  return path;
};

/**
 * Same-origin /@@images path. Absolute API URLs drop the logged-in session.
 */
const toSiteImagePath = (path) => {
  if (!path) return '';
  let value = String(path);
  const internalApiPath = config.settings.internalApiPath || '';
  if (internalApiPath && value.startsWith(internalApiPath)) {
    value = value.slice(internalApiPath.length) || '/';
  }

  if (
    (value.startsWith('http://') || value.startsWith('https://')) &&
    !isPloneContentUrl(value)
  ) {
    return normalizeImageScalePath(value);
  }

  let normalized = normalizeAppUrl(value) || '';
  if (!normalized) return '';
  if (normalized.startsWith('http://') || normalized.startsWith('https://')) {
    return normalizeImageScalePath(normalized);
  }
  if (!normalized.startsWith('/')) {
    normalized = `/${normalized}`;
  }
  normalized = stripApiMount(normalized);
  if (!normalized.startsWith('/')) {
    normalized = `/${normalized}`;
  }
  return normalizeImageScalePath(normalized);
};

const newItemId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const inferImageType = (imageRef = '') => {
  const ref = String(imageRef).toLowerCase();
  if (ref.includes('home-slider-images')) {
    return 'imatge_slider_principal';
  }
  if (ref.includes('icones-pagina-inicial')) {
    return 'icona_rectificada_pagina_principal';
  }
  if (ref.includes('unitats-especialitzades')) {
    return 'unitatespecialitzada';
  }
  return 'Image';
};

export const normalizeImageRef = (image) => {
  if (!image) return image;
  let ref = image;
  if (typeof image === 'string') {
    ref = { '@id': image };
  } else if (Array.isArray(image) && image[0]?.['@id']) {
    ref = image[0];
  }
  if (ref['@type']) {
    return ref;
  }
  const imageId = ref['@id'] || '';
  return {
    ...ref,
    '@type': inferImageType(imageId),
    title: ref.title || ref.Title || ref.filename || '',
  };
};

export const normalizeListItem = (item) => {
  if (!item || typeof item !== 'object') return item;
  const normalized = {
    ...item,
    '@id': item['@id'] || newItemId(),
  };
  if (item.image !== undefined) {
    normalized.image = normalizeImageRef(item.image);
  }
  return normalized;
};

export const normalizeObjectList = (items = []) => items.map(normalizeListItem);

export const listItemsNeedNormalization = (items = []) =>
  items.some(
    (item) =>
      !item?.['@id'] ||
      typeof item?.image === 'string' ||
      Array.isArray(item?.image) ||
      (item?.image && !item.image['@type']),
  );

const toApiImageUrl = (ref) => {
  if (!ref) return '';
  const path = toSiteImagePath(ref);
  if (!path || path.includes('/@@images/')) return path;
  return `${path.replace(/\/$/, '')}/@@images/image`;
};

export const resolveHomepageImage = (image) => {
  if (!image) return '';
  if (typeof image === 'object') {
    const ref = image[0]?.['@id'] || image['@id'];
    if (ref) return toApiImageUrl(ref);
  }
  if (image.startsWith('resolveuid/')) {
    const uid = image.replace('resolveuid/', '');
    return toApiImageUrl(`/resolveuid/${uid}`);
  }
  if (image.startsWith('http') || image.startsWith('/')) {
    return toApiImageUrl(image);
  }
  return toApiImageUrl(`/resolveuid/${image}`);
};

const FALLBACK_SCALE_WIDTH = {
  large: 800,
  preview: 400,
  thumb: 128,
};

const MAX_SCALE_WIDTH = 2000;

const imageObjectId = (image) => {
  if (!image || typeof image !== 'object') return '';
  return image['@id'] || image[0]?.['@id'] || '';
};

const hasExplicitScale = (url) => /\/@@images\/image[/-]/.test(url || '');

const publicScaleUrl = (objectId, download) => {
  const raw = String(download || '');
  if (!raw) return '';
  if (
    raw.startsWith('http://') ||
    raw.startsWith('https://') ||
    raw.startsWith('/')
  ) {
    return toSiteImagePath(raw);
  }
  if (!objectId) return '';
  const base = toSiteImagePath(objectId).replace(/\/$/, '');
  return toSiteImagePath(`${base}/${raw.replace(/^\//, '')}`);
};

const scaleRecord = (image) => {
  const scales = image?.image_scales?.image;
  const record = Array.isArray(scales) ? scales[0] : null;
  if (record?.download || record?.scales) {
    return record;
  }
  if (image?.scales || image?.download) {
    return {
      download: image.download,
      width: image.width,
      height: image.height,
      scales: image.scales,
    };
  }
  return null;
};

const scaleCandidates = (record) => {
  const items = [];
  if (record.download && record.width) {
    items.push({
      download: record.download,
      width: record.width,
      height: record.height,
    });
  }
  Object.values(record.scales || {}).forEach((scale) => {
    if (scale?.download && scale.width) {
      items.push(scale);
    }
  });
  const byWidth = new Map();
  items.forEach((item) => {
    if (!byWidth.has(item.width)) {
      byWidth.set(item.width, item);
    }
  });
  return [...byWidth.values()]
    .filter((item) => item.width <= MAX_SCALE_WIDTH)
    .sort((a, b) => a.width - b.width);
};

/**
 * src is the smallest stored scale that covers minWidth.
 * srcSet keeps that scale and any wider one.
 * Without image_scales, append a named scale once.
 */
export const homepageImageSources = (
  image,
  { minWidth = 800, fallback = 'large' } = {},
) => {
  const record = scaleRecord(image);
  const objectId = imageObjectId(image);
  if (record) {
    const candidates = scaleCandidates(record).filter((item) =>
      publicScaleUrl(objectId, item.download),
    );
    if (candidates.length) {
      const chosen =
        candidates.find((item) => item.width >= minWidth) ||
        candidates[candidates.length - 1];
      const src = publicScaleUrl(objectId, chosen.download);
      const srcSet = candidates
        .filter((item) => item.width >= chosen.width)
        .map(
          (item) => `${publicScaleUrl(objectId, item.download)} ${item.width}w`,
        )
        .join(', ');
      return {
        src,
        srcSet,
        width: chosen.width,
        height: chosen.height,
      };
    }

    const named = record.scales?.[fallback];
    const src = publicScaleUrl(objectId, named?.download);
    if (src) {
      const width = named.width || FALLBACK_SCALE_WIDTH[fallback];
      return {
        src,
        srcSet: width ? `${src} ${width}w` : undefined,
        width,
        height: named.height,
      };
    }
  }

  if (!image || (typeof image === 'object' && !objectId)) {
    return { src: '', srcSet: undefined, width: undefined, height: undefined };
  }

  const base = resolveHomepageImage(image);
  if (!base) {
    return { src: '', srcSet: undefined, width: undefined, height: undefined };
  }
  const src = hasExplicitScale(base) ? base : `${base}/${fallback}`;
  const nominal = FALLBACK_SCALE_WIDTH[fallback];
  return {
    src,
    srcSet: nominal ? `${src} ${nominal}w` : undefined,
    width: nominal,
    height: undefined,
  };
};

export const splitLines = (text, className) =>
  (text ?? '').split('\\n').map((line, index) => (
    <p key={index} className={className}>
      {line}
    </p>
  ));
